import { Onboarding } from '@/onboarding/layout/OnboardingPageTemplate'
import { LabelInput } from '@/common/ui/LabelInput'
import { Spinner } from '@/common/ui/spinner'
import type { BankAccountFormInput } from '@/onboarding/types'
import { useForm } from 'react-hook-form'
import { useEffect } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { bankAccountSchema, BankAccountFormValues } from '../rules/bankAccountSchema'
import { ONBOARDING_COPY } from '@/locales/en'

export type PageProps = {
    progress?: number
    defaultValues?: BankAccountFormInput
    errors?: Partial<Record<'bankAccountName' | 'bsb' | 'bankAccountNumber', string>>
    loading?: boolean
    saving?: boolean
    disabled?: boolean
}

interface ViewModelProps extends PageProps {
    /** Form submission handler — *required for production use*; may be omitted in Storybook previews where only form state is displayed. */
    onSubmit?: (data: BankAccountFormValues) => void
}

export function LinkBankAccountForm({
    progress = 45,
    defaultValues,
    errors,
    loading,
    saving,
    disabled,
    onSubmit,
}: ViewModelProps) {
    const {
        register,
        handleSubmit,
        setFocus,
        reset,
        formState: { errors: formErrors },
    } = useForm<BankAccountFormValues>({
        resolver: yupResolver(bankAccountSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues,
    })

    useEffect(() => {
        if (defaultValues) {
            reset(defaultValues)
        }
    }, [defaultValues, reset])

    useEffect(() => {
        setFocus('bankAccountName')
    }, [setFocus])

    const displayErrors: PageProps['errors'] =
        errors ??
        ({
            bankAccountName: formErrors.bankAccountName?.message,
            bsb: formErrors.bsb?.message,
            bankAccountNumber: formErrors.bankAccountNumber?.message,
        } satisfies PageProps['errors'])
    const isDisabled = disabled ?? (loading || saving)

    if (loading && !defaultValues) return <Spinner size="lg" />

    return (
        <Onboarding
            title={ONBOARDING_COPY.linkBankAccount.title}
            isLoading={saving || loading}
            cta={ONBOARDING_COPY.linkBankAccount.cta}
            description={ONBOARDING_COPY.linkBankAccount.description}
            onSubmit={onSubmit ? handleSubmit(onSubmit) : () => {}}
            disabled={isDisabled}
            progress={progress}
        >
            <>
                <LabelInput
                    {...register('bankAccountName')}
                    type="text"
                    id="bankAccountName"
                    required
                    formNoValidate
                    label={ONBOARDING_COPY.linkBankAccount.form.accountName}
                    placeholder={ONBOARDING_COPY.linkBankAccount.form.accountNamePlaceholder}
                    helperText={ONBOARDING_COPY.linkBankAccount.form.accountNameHelperText}
                    defaultValue={defaultValues?.bankAccountName}
                    error={displayErrors?.bankAccountName}
                />
                <LabelInput
                    {...register('bsb')}
                    type="text"
                    id="bsb"
                    required
                    formNoValidate
                    label={ONBOARDING_COPY.linkBankAccount.form.bsb}
                    placeholder={ONBOARDING_COPY.linkBankAccount.form.bsbPlaceholder}
                    maxLength={7}
                    defaultValue={defaultValues?.bsb}
                    error={displayErrors?.bsb}
                />
                <LabelInput
                    {...register('bankAccountNumber')}
                    type="text"
                    id="bankAccountNumber"
                    required
                    formNoValidate
                    label={ONBOARDING_COPY.linkBankAccount.form.accountNumber}
                    placeholder={ONBOARDING_COPY.linkBankAccount.form.accountNumberPlaceholder}
                    error={displayErrors?.bankAccountNumber}
                />
            </>
        </Onboarding>
    )
}
